import * as React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import moment from "moment";

import { MapEditorRouteContext } from "../CustomRoutes/MapEditor";
import { Context as InteractionContext } from "./Interaction/InteractionContext";
import { Context as InstanceDataContext } from "./Positionable/InstanceDataContext";
import {  useEffect , useState , useContext, useMemo, useRef} from "react";
import { useQuery } from "react-query";
import { Prompt } from "react-router-dom";
import positionableApi from "../../utils/api/positionable";
import { Modal } from "@stormwater-software/lib-ui";
import RolesContext from "../../contexts/RolesContext";
import { Context as DataSourceContext } from '../../components/MapEditor/DataSourceContext';
import ConfirmationModal from '../Shared/ConfirmationModal/ConfirmationModal';
import Toggle from '../Shared/Toggle/toggle';
import { buildVersionOptions, versionDayOf } from "./mapVersionOptions";
import { MapVersion } from "./mapVersionOptions";

export const Header = () => {

    const mapRouteContext = useContext(InstanceDataContext);
    const interactionContext = useContext(InteractionContext);
    const mapEditorRouteContext = useContext(MapEditorRouteContext);
    const dataSource = useContext(DataSourceContext);
    const [closeModal , setCloseModal] = useState<boolean>(false)
    const [inspectionChangeModal, setInspectionChangeModal] = useState<boolean>(false)
    const [pendingVersion, setPendingVersion] = useState<string | null>(null)
    const [selectedVersion, setSelectedVersion] = useState<string>("")
    const onLatestVersionRef = useRef<boolean>(true)
    const permCheck = useContext(RolesContext).userHasPermission;
    const { showRemoved, setShowRemoved, projectId, projectUploadId, selectedInspection, setSelectedInspection, setSelectedVersionDate, setSelectedVersionInspectionId, setIsLatestMapVersion } = mapEditorRouteContext;

    const mapVersionsQuery = useQuery<MapVersion[]>(
      ["project", "map-versions", projectId, projectUploadId],
      () => positionableApi.mapVersions(projectId, projectUploadId),
      { enabled: Boolean(projectId && projectUploadId) },
    );

    const versionOptions = useMemo(
      () => buildVersionOptions(mapVersionsQuery.data || []),
      [mapVersionsQuery.data],
    );

    useEffect(()=>{
      window.onbeforeunload =(e:any)=>{
        if(mapRouteContext.hasPendingUpdates){
          e.preventDefault();
          e.returnValue = true;
        }
      }
    })

    const onContinue = () => {
      mapRouteContext.discard();
      interactionContext.setPositionableType(null);
      setCloseModal(false) 
      mapEditorRouteContext.goBack()
    }

    const openModal = () => {
      setCloseModal(true)
    }

    const latestVersionFor = (inspectionId: string) =>
      versionOptions.find((option) => String(option.inspectionId) === inspectionId);

    const applyMapVersion = (value: string) => {
      const [inspectionId, versionDate] = value.split("|");

      onLatestVersionRef.current = value === latestVersionFor(inspectionId)?.value;

      /**
       * SW2-2346, corrected in SW2-2347: the newest DAY may be edited, not the single
       * newest option.
       *
       * The dedupe deliberately keeps one entry per inspection when two inspections share
       * a day, so the newest day can carry several values. Comparing the whole value made
       * exactly one of them "latest" and every other a read-only older map — on the day
       * that IS the current one. It also propagates now: Toolbar carries a version date to
       * the next map whenever this is false, so the wrong twin sent the whole rest of the
       * project back in time.
       *
       * The inspection id stays in the value, because it still selects which findings
       * render; only the latest-ness test drops it. Falls back to the old comparison when
       * the newest option carries no day, which is the fallback entry for an inspection
       * that owns no version.
       */
      const newestDay = versionDayOf(versionOptions[0]?.value);

      setIsLatestMapVersion?.(
        newestDay ? versionDayOf(value) === newestDay : value === versionOptions[0]?.value,
      );

      setSelectedVersion(value);
      setSelectedInspection(inspectionId);
      setSelectedVersionDate(versionDate || null);
      setSelectedVersionInspectionId(Number(inspectionId));
    }

    /**
     * SW2-2347 — step through versions one at a time.
     *
     * The list is newest-first, so "up" is newer and "down" is older, matching the order
     * on screen rather than chronology. Both go through handleMapVersionChange, NOT
     * applyMapVersion: that is what puts up the confirmation when there are unsaved map
     * edits, and a stepper that bypassed it would discard someone's work on a click.
     *
     * Stepping earned its place with this ticket. Recovering versions that were
     * unreachable takes some pickers from 2 entries to 25, and hunting a specific day in
     * a long native select — especially to compare one version against the one beside it
     * — is worse than pressing an arrow twice.
     */
    const currentVersionIndex = versionOptions.findIndex(
      (option) => option.value === selectedVersion,
    );

    const stepVersion = (delta: number) => {
      if (currentVersionIndex < 0) return;

      const next = versionOptions[currentVersionIndex + delta];

      if (next) handleMapVersionChange(next.value);
    };

    const handleMapVersionChange = (value: string) => {
      if(dataSource.undo && dataSource.undo.length){
        setPendingVersion(value);
        setInspectionChangeModal(true);
      }else{
        applyMapVersion(value);
      }
    }

    const handleInspectionChangeConfirm = () => {
      // Clear the undo state
      dataSource.setUndo([]);
      if (pendingVersion) {
        applyMapVersion(pendingVersion);
      }
      setInspectionChangeModal(false);
      setPendingVersion(null);
    }

    const handleInspectionChangeCancel = () => {
      setInspectionChangeModal(false);
      setPendingVersion(null);
    }
    
    useEffect(() => {
      if (!selectedVersion || !onLatestVersionRef.current) return;

      const latest = latestVersionFor(selectedVersion.split("|")[0]);

      if (latest && latest.value !== selectedVersion) applyMapVersion(latest.value);
    }, [versionOptions]);

    useEffect(() => {
      if (selectedVersion || !versionOptions.length) return;

      /**
       * SW2-2347 — resume the version the previous map was on, when one was carried.
       *
       * The map arrows navigate with a full page load, so the open version can only reach
       * us through the URL (see Toolbar). Maps do not share a day list — each has its own
       * history — so an exact match is not something to rely on. The newest day on or
       * before the carried one is the same question the server answers when it renders a
       * map "as of" a date, and it is what paging through a project at a point in time
       * means.
       *
       * versionOptions is newest-first and every day is yyyy-MM-dd, so the first match
       * scanning down IS the newest one at or before the carried day. Entries with no day
       * are skipped: an inspection that owns no version sorts last and would otherwise
       * match everything.
       */
      const params = new URLSearchParams(window.location.search);
      /**
       * The shape check is not enough on its own: `2026-02-31` matches the regex and is
       * not a day, and it would then be compared lexicographically as though it were,
       * resuming on whichever version happens to sort below it. Strict parsing decides
       * whether the shape names a day that exists — the same pairing the backend uses for
       * every client-supplied date.
       */
      const rawCarriedDay = params.get("versionDate");
      const carriedDay =
        rawCarriedDay &&
        /^\d{4}-\d{2}-\d{2}$/.test(rawCarriedDay) &&
        moment.utc(rawCarriedDay, "YYYY-MM-DD", true).isValid()
          ? rawCarriedDay
          : null;
      const hasADay = (option: (typeof versionOptions)[number]) =>
        (option.value.split("|")[1] || "") !== "";

      /**
       * Nothing on or before the carried day means this map has no history that far back —
       * it was added to the project later. Its OLDEST version is the closest thing to the
       * day that was asked for; falling through to the newest would land the user at the
       * far end of the timeline from the one they chose, silently.
       */
      const resumed = carriedDay
        ? versionOptions.find(
            (option) => hasADay(option) && (option.value.split("|")[1] || "") <= carriedDay,
          ) || [...versionOptions].reverse().find(hasADay)
        : undefined;

      const initial =
        resumed ||
        versionOptions.find((option) => option.inspectionId === selectedInspection?.id) ||
        versionOptions[0];

      /**
       * Spend the parameter once. Left in the address bar it outlives the navigation that
       * set it: pick the newest version, reload, and this effect silently drags you back
       * to the carried day — read-only, saves back-dated, nothing on screen saying why.
       * It also rides along in any link copied from the bar.
       */
      if (rawCarriedDay) {
        params.delete("versionDate");

        const query = params.toString();

        // Keep whatever state the router put here. Replacing it with {} drops
        // `location.state`, which carries the inspectionId MapEditor reads to decide
        // which inspection to open — so a reload after arriving via the arrows would
        // land on the wrong one.
        window.history.replaceState(
          window.history.state,
          "",
          `${window.location.pathname}${query ? `?${query}` : ""}`,
        );
      }

      applyMapVersion(initial.value);
    }, [versionOptions, selectedInspection, selectedVersion]);

    return (
        <MapEditorRouteContext.Consumer>
          {({
            mapName,
            projectName,
            updatedAt,
            goBack,
          }) => (
            <div className="map-editor-header">
              <div className="map-editor-header-left">
                <h1>{projectName}</h1>
                <h2>{mapName}</h2>
                <div
                  className={`map-editor-show-removed-toggle map-editor-show-removed-mobile${showRemoved ? ' map-editor-show-removed-toggle--active' : ''}`}
                >
                  <span>Show Removed Items</span>
                  <div className="map-editor-show-removed-toggle__control">
                    <Toggle
                      id="show-removed-toggle-mobile"
                      isChecked={showRemoved}
                      handleChange={() => setShowRemoved(!showRemoved)}
                    />
                  </div>
                </div>
              </div>

              <div className="map-editor-header-right">
                <div className="map-editor-header-info">
                  <div
                    className={`map-editor-show-removed-toggle map-editor-show-removed-desktop${showRemoved ? ' map-editor-show-removed-toggle--active' : ''}`}
                  >
                    <span>Show Removed Items</span>
                    <div className="map-editor-show-removed-toggle__control">
                      <Toggle
                        id="show-removed-toggle-desktop"
                        isChecked={showRemoved}
                        handleChange={() => setShowRemoved(!showRemoved)}
                      />
                    </div>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                    {versionOptions.length > 0 &&(
                      <InteractionContext.Consumer>
                        {({ mode }) => (
                          <InstanceDataContext.Consumer>
                            {({ hasPendingUpdates }) => (
                              <div className="map-version-picker">
                                <select
                                  className="map-select-inspection"
                                  value={selectedVersion}
                                  onChange={e=>handleMapVersionChange(e.target.value)}
                                  disabled={hasPendingUpdates || mode === "create"}
                                  title="Active Inspection"
                                >
                                  <option value="" disabled>{selectedVersion ? "" : "Select a map version"}</option>
                                  {versionOptions.map(({ value, label }) => (
                                    <option value={value} key={value}>{label}</option>
                                  ))}
                                </select>
                                {/* Disabled at the ends rather than wrapping: on a list of
                                    25 versions, silently jumping from the oldest to the
                                    newest reads as a bug. */}
                                <button
                                  type="button"
                                  className="map-version-step"
                                  aria-label="Newer version"
                                  title="Newer version"
                                  onClick={() => stepVersion(-1)}
                                  disabled={
                                    hasPendingUpdates ||
                                    mode === "create" ||
                                    currentVersionIndex <= 0
                                  }
                                >
                                  ▲
                                </button>
                                <button
                                  type="button"
                                  className="map-version-step"
                                  aria-label="Older version"
                                  title="Older version"
                                  onClick={() => stepVersion(1)}
                                  disabled={
                                    hasPendingUpdates ||
                                    mode === "create" ||
                                    currentVersionIndex < 0 ||
                                    currentVersionIndex >= versionOptions.length - 1
                                  }
                                >
                                  ▼
                                </button>
                              </div>
                            )}
                          </InstanceDataContext.Consumer>
                        )}
                      </InteractionContext.Consumer>
                    )}
                    <span className="map-updated-at">{(permCheck("all", "Map Drawing") || permCheck("update", "Map Drawing")) && "Last Saved:" + updatedAt}</span>
                  </div>
                </div>

                <span
                  className="map-close pure-button button-transparent"
                  onClick={mapRouteContext.hasPendingUpdates ? openModal : goBack}
                >
                  <FontAwesomeIcon icon="times-circle" />
                </span>
              </div>

              <ConfirmationModal
                show={inspectionChangeModal}
                handleClose={handleInspectionChangeCancel}
                handleConfirm={handleInspectionChangeConfirm}
                title='Change Inspection Version'
                subTitle='Changing the inspection version will clear your undo history. You will not be able to undo any removed legend items after this change.'
                buttonText='Confirm'
              />

              
              {closeModal && 
              <Modal
                handleClose={()=>setCloseModal(false)}
                handleSubmit={onContinue}
                submitBtnText = {"Continue"}
                show={true}
                title={"You have unsaved changes"}
                hideButtons={false}
                cancelBtn={true}
                style={{textAlign:'center'}}
              >
                <p>
                  You have some unsaved changes. Are you sure you want to exit?
                </p>
                <p>
                <small>*Disclaimer : By selecting continue all unsaved changes will be lost.</small>
                </p>
              </Modal>
              }
              <Prompt 
              when={mapRouteContext.hasPendingUpdates}

              message="You have unchanged map edit.Are you sure you want to exit?" />
            </div>
          )}
        </MapEditorRouteContext.Consumer>
    )
}

Header.displayName = "MapEditorHeader";
